import React, { useCallback, useEffect, useState } from 'react';

import { getUncategorisedProducts } from '../../services/categoriesApi';
import CategoryAssignEditor from './CategoryAssignEditor';

const PAGE_SIZE = 50;

const NoCategoryQueue = () => {
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (off) => {
      setLoading(true);
      try {
        const d = await getUncategorisedProducts({ limit: PAGE_SIZE, offset: off });
        setData(d);
        setError(null);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    load(offset);
  }, [load, offset]);

  const refresh = () => load(offset);

  if (error) return <div className="error">Error: {error}</div>;
  if (!data) return <div>Loading queue…</div>;

  return (
    <div className="sync-dashboard">
      <header>
        <h1>No-Category queue</h1>
        <span className="muted">
          {data.total.toLocaleString()} SKUs in the <code>{data.holding_bucket_code}</code> holding
          area or with no <code>akeneo_categories</code>. Success = 0 remaining.
        </span>
      </header>

      <section className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <button type="button" onClick={refresh} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <span className="muted">
            Showing {Math.min(data.total, data.offset + 1)}–
            {Math.min(data.total, data.offset + data.items.length)} of{' '}
            {data.total.toLocaleString()}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={data.offset === 0}
            >
              ← Prev
            </button>
            <button
              type="button"
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              disabled={!data.has_more}
            >
              Next →
            </button>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Name</th>
              <th>Brand</th>
              <th>Lifecycle</th>
              <th>Current categories</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.sku}>
                <td><code>{p.sku}</code></td>
                <td>{p.product_name || p.name}</td>
                <td>{p.brand}</td>
                <td>{p.product_lifecycle}</td>
                <td>
                  {(p.akeneo_categories || []).map((c) => (
                    <code key={c} style={{ marginRight: 4 }}>{c}</code>
                  ))}
                  {(!p.akeneo_categories || p.akeneo_categories.length === 0) && (
                    <span className="muted">(none)</span>
                  )}
                </td>
                <td>
                  <button type="button" onClick={() => setEditing(p.sku)}>
                    Assign…
                  </button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  The queue is empty — no uncategorised SKUs remaining.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {editing && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: 'min(520px, 100%)',
            background: '#101826',
            borderLeft: '1px solid #2a3a52',
            boxShadow: '-4px 0 16px rgba(0,0,0,0.4)',
            zIndex: 50,
            overflowY: 'auto',
            padding: 16,
          }}
        >
          <button
            type="button"
            onClick={() => setEditing(null)}
            style={{ float: 'right' }}
          >
            ✕
          </button>
          <CategoryAssignEditor
            sku={editing}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default NoCategoryQueue;
